import { style } from "@vanilla-extract/css";
// The menu ships on every page, so it carries the global display-mode guard.
import "@/client/ui/components/display-mode/display-mode.css";
import { kCompactSelector } from "@/client/utils/displayMode";
import { kCompactTriggerTop_px } from "@/client/utils/headerMetrics";
import { borderRadius } from "@/client/ui/design-system/styles/border-radius.css";
import { colors } from "@/client/ui/design-system/styles/colors.css";
import { spacing } from "@/client/ui/design-system/styles/spacing.css";
import { body1Css, body2Css, body3Css } from "@/client/ui/design-system/styles/typography.css";
import { px } from "@/client/utils/units";

// ── Root (fixed anchor) ───────────────────────────────────────────────────────

export const root = style({
    position: "fixed",
    top: spacing.sm,
    right: spacing.sm,
    zIndex: 1000,
    paddingTop: px(16),
    paddingRight: spacing.sm,
    selectors: {
        // Center the trigger against the compact chrome bar (see
        // headerMetrics.ts — home and app headers share the same height) and
        // hug the panel's right edge.
        [kCompactSelector]: {
            top: 0,
            paddingTop: px(kCompactTriggerTop_px),
            right: 0,
            paddingRight: spacing.xs,
        },
    },
});

// ── Panel ─────────────────────────────────────────────────────────────────────

export const panel = style({
    position: "absolute",
    top: 0,
    right: 0,
    width: px(236),
    backgroundColor: colors.gray90,
    borderWidth: px(1),
    borderStyle: "solid",
    borderColor: colors.gray70,
    borderRadius: borderRadius.lg,
    boxShadow: `0 ${px(12)} ${px(40)} rgba(0, 0, 0, 0.55)`,
    overflow: "hidden",
    opacity: 0,
    visibility: "hidden",
    transform: "translateY(-6px) scale(0.97)",
    transformOrigin: "top right",
    transition: "opacity 0.15s ease, transform 0.15s ease, visibility 0s linear 0.15s",
    pointerEvents: "none",
    selectors: {
        // Embedded panel: the menu opens as a full overlay modal instead of a
        // right-aligned popover — the narrow panel has no width to spare.
        [kCompactSelector]: {
            position: "fixed",
            inset: 0,
            width: "auto",
            borderRadius: 0,
            borderWidth: 0,
            overflowY: "auto",
        },
    },
});

export const panelOpen = style({
    opacity: 1,
    visibility: "visible",
    transform: "translateY(0) scale(1)",
    transition: "opacity 0.15s ease, transform 0.15s ease",
    pointerEvents: "auto",
});

// ── Panel body ────────────────────────────────────────────────────────────────

export const panelBody = style({});

// ── Home link row ─────────────────────────────────────────────────────────────

export const homeLinkRow = style({
    display: "flex",
    alignItems: "center",
    gap: spacing.xs,
    paddingTop: px(22),
    paddingBottom: px(14),
    paddingLeft: spacing.md,
    paddingRight: spacing.md,
});

// ── Trigger button ────────────────────────────────────────────────────────────

export const trigger = style({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: px(10),
    backgroundColor: colors.gray90,
    borderWidth: px(1),
    borderStyle: "solid",
    borderColor: colors.gray70,
    borderRadius: borderRadius.lg,
    color: colors.white,
    cursor: "pointer",
    position: "relative",
    zIndex: 1,
    transition: "border-color 0.15s ease, color 0.15s ease",
    ":hover": {
        borderColor: colors.gray50,
    },
    selectors: {
        // The embedded panel trigger is a bare glyph — no box. Geometry
        // (padding + border) stays identical so the centering math in
        // headerMetrics.ts holds; only the paint goes away.
        [kCompactSelector]: {
            backgroundColor: "transparent",
            borderColor: "transparent",
            color: colors.gray30,
        },
        [`${kCompactSelector}:hover`]: {
            borderColor: "transparent",
            color: colors.white,
        },
    },
});

export const triggerIcon = style({
    width: px(16),
    height: px(16),
    flexShrink: 0,
});

// Closed-state glyphs: hamburger on the web, info in the embedded panel
// (there the menu is app details, not site navigation). Both are in the DOM;
// display mode picks one.
export const triggerIconWeb = style([
    triggerIcon,
    {
        selectors: {
            [kCompactSelector]: {
                display: "none",
            },
        },
    },
]);

export const triggerIconCompact = style([
    triggerIcon,
    {
        display: "none",
        selectors: {
            [kCompactSelector]: {
                display: "block",
            },
        },
    },
]);

// ── Account (website home) ───────────────────────────────────────────────────

/**
 * The trigger as the signed-in person's avatar: a 36px circle in place of the
 * hamburger's box, and it stays put while the menu is open rather than turning
 * into an x, so the person reads as the thing the menu is about.
 */
export const triggerAvatar = style({
    padding: 0,
    width: px(36),
    height: px(36),
    borderRadius: "50%",
    overflow: "hidden",
    backgroundColor: colors.gray80,
});

export const avatar = style({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    overflow: "hidden",
});

export const avatarImage = style({
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
});

export const avatarInitials = style({
    ...body3Css,
    fontSize: px(13),
    fontWeight: 600,
    lineHeight: "1",
    letterSpacing: px(0.3),
    color: colors.white,
});

/** Who is signed in, their tier, and the way out, at the top of the panel. */
export const accountSection = style({
    display: "flex",
    flexDirection: "column",
    gap: px(6),
    // Room at the top for the avatar trigger, which overlaps the panel's corner.
    padding: `${px(22)} ${spacing.md} ${spacing.md}`,
    paddingRight: px(64),
});

export const accountName = style({
    ...body1Css,
    fontWeight: 600,
    color: colors.white,
    margin: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
});

export const accountEmail = style({
    ...body3Css,
    color: colors.gray40,
    margin: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
});

/** The tier chip, left-aligned under the name rather than centred as it was beside the title. */
export const accountTierRow = style({
    display: "flex",
    alignItems: "center",
    marginTop: px(2),
});

/** The foot of the account menu: Log out, under the tools and build info. */
export const accountActions = style({
    display: "flex",
    padding: spacing.md,
});

// ── Home link ─────────────────────────────────────────────────────────────────

export const homeLink = style({
    display: "flex",
    alignItems: "center",
    gap: spacing.xs,
    textDecoration: "none",
    color: colors.white,
    transition: "color 0.12s ease",
    ":hover": {
        color: colors.gray30,
    },
});

export const homeLinkIcon = style({
    width: px(18),
    height: px(18),
    flexShrink: 0,
});

export const homeLinkText = style({
    ...body2Css,
    fontWeight: 500,
    color: "inherit",
    margin: 0,
});

// ── Divider ───────────────────────────────────────────────────────────────────

export const divider = style({
    border: "none",
    borderTopWidth: px(1),
    borderTopStyle: "solid",
    borderTopColor: colors.gray80,
    margin: 0,
});

// ── Sections ──────────────────────────────────────────────────────────────────

export const section = style({
    display: "flex",
    flexDirection: "column",
    gap: spacing.xs,
    padding: spacing.md,
});

export const devToolsSection = style({
    display: "flex",
    flexDirection: "column",
    gap: px(12),
    padding: `${spacing.lg} ${spacing.md} 0 ${spacing.md}`,
});

export const devToolsSectionLabel = style({
    ...body3Css,
    fontSize: px(11),
    lineHeight: "1",
    letterSpacing: px(0.9),
    textTransform: "uppercase",
    color: colors.gray60,
    margin: 0,
    marginBottom: px(4),
});

export const sectionLabel = style({
    ...body3Css,
    fontSize: px(11),
    lineHeight: "1",
    letterSpacing: px(0.9),
    textTransform: "uppercase",
    color: colors.gray60,
    margin: 0,
});

// ── About section ─────────────────────────────────────────────────────────────

export const appTitle = style({
    ...body1Css,
    fontWeight: 600,
    color: colors.white,
    margin: 0,
});

export const appDescription = style({
    ...body2Css,
    color: colors.gray40,
    margin: `${spacing.sm} 0`,
    lineHeight: "1.5",
});

export const tagRows = style({
    display: "flex",
    flexDirection: "column",
    gap: px(6),
});

export const tagList = style({
    display: "flex",
    flexWrap: "wrap",
    gap: px(4),
});

const tagBase = {
    ...body3Css,
    fontSize: px(10),
    lineHeight: "1",
    padding: `${px(3)} ${px(7)}`,
    borderRadius: borderRadius.sm,
    borderWidth: px(1),
    borderStyle: "solid" as const,
    backgroundColor: "transparent",
    fontWeight: 500,
    flexShrink: 0,
};

export const tag = style({
    ...tagBase,
    color: colors.gray40,
});

export const tagV2 = style({
    ...tagBase,
    color: colors.blue30,
});

export const tagV25 = style({
    ...tagBase,
    color: colors.orange05,
});

export const tagV3 = style({
    ...tagBase,
    color: colors.purple20,
});

export const tagV4 = style({
    ...tagBase,
    color: colors.green20,
});

export const tagModalMain = style({
    ...tagBase,
    color: colors.gray50,
});

export const tagModalProd = style({
    ...tagBase,
    color: colors.green30,
});

export const tagAtlantis = style({
    ...tagBase,
    color: colors.purple30,
});

export const tagMusicAi = style({
    ...tagBase,
    color: colors.red30,
});

// ── Dev tools section ─────────────────────────────────────────────────────────

export const devToolFeatured = style({
    ...body2Css,
    fontWeight: 500,
    color: colors.white,
    textDecoration: "none",
    display: "block",
    margin: 0,
    transition: "color 0.12s ease",
    ":hover": {
        color: colors.gray30,
    },
});

export const devToolLink = style({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.xs,
    textDecoration: "none",
    color: colors.gray40,
    transition: "color 0.12s ease",
    ":hover": {
        color: colors.white,
    },
});

export const devToolLinkLabel = style({
    ...body3Css,
    color: "inherit",
    margin: 0,
});

export const devToolLinkIcon = style({
    width: px(10),
    height: px(10),
    color: colors.gray60,
    flexShrink: 0,
});

// ── Developer section ─────────────────────────────────────────────────────────

export const devLink = style({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.xs,
    textDecoration: "none",
    color: colors.gray40,
    transition: "color 0.12s ease",
    ":hover": {
        color: colors.white,
    },
});

export const devLinkLabel = style({
    ...body3Css,
    color: "inherit",
    margin: 0,
});

export const devLinkIcon = style({
    width: px(10),
    height: px(10),
    color: colors.gray60,
    flexShrink: 0,
});

// ── Build info ────────────────────────────────────────────────────────────────

export const buildInfoSection = style({
    display: "flex",
    flexDirection: "column",
    gap: px(3),
    padding: `${spacing.sm} ${spacing.md}`,
});

export const buildInfoLine = style({
    fontSize: px(10),
    lineHeight: "1.4",
    color: colors.gray60,
    fontFamily: "monospace",
    overflow: "hidden",
    textWrapMode: "wrap",
    margin: 0,
});

/** App-supplied controls in the details panel, under the tags. */
export const actionRow = style({
    display: "flex",
    flexWrap: "wrap",
    gap: spacing.xs,
    marginTop: spacing.sm,
});
